import { CodingExamEntranceIllustration } from '../../../../../assets/illustrations';
import { ArrowRightIcon, WarningIcon } from '../../../../../assets/icons/common';
import { setExamRulesModal } from '../../../../../store/exam/slice';
import RadioButton from '../../../../../components/RadioButton';
import { useDispatch } from '../../../../../store/store';
import DropDown from '../../../../../components/DropDown';
import Button from '../../../../../components/Button';
import { ExamLanguage, hasLanguageChoice as hasExamLanguageChoice } from '../../../constants';
import { QUESTION_COUNT } from '../../constants';
import styles from './style.module.scss';

const selectLanguageText = 'To take the coding exam, select a programming language';
const startExamText = 'Press the start button when you’re ready';

interface Props {
    langs: string[];
    loading: boolean;
    action?: string;
    selectedLang: string;
    examLanguage: string;
    examLanguages: ExamLanguage[];
    cameraAccess: boolean;
    isCameraActive: boolean;
    isScreenRecording: boolean;
    screenRecordFailed: boolean;
    onStart: () => void;
    onEnableCamera: () => void;
    onStartScreenRecord: () => void;
    onSelectLang: (lang: string) => void;
    onSelectExamLanguage: (language: string) => void;
};

/** Entrance screen: what the exam involves, the permissions it needs, and the start button. */
function PreExam(props: Props) {
    const {
        langs,
        action,
        loading,
        onStart,
        selectedLang,
        examLanguage,
        examLanguages,
        cameraAccess,
        onSelectLang,
        isCameraActive,
        onEnableCamera,
        isScreenRecording,
        screenRecordFailed,
        onStartScreenRecord,
        onSelectExamLanguage,
    } = props;

    const dispatch = useDispatch();
    const hasLanguageChoice = langs.length > 1;

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <CodingExamEntranceIllustration />

                <span className={styles.title}>
                    {hasLanguageChoice ? selectLanguageText : startExamText}
                </span>

                {hasLanguageChoice && (<div className={styles.languages}>
                    {langs.map(lang => <RadioButton
                        key={lang}
                        text={lang}
                        checked={selectedLang === lang}
                        setChecked={() => onSelectLang(lang)}
                    />)}
                </div>)}

                <p className={styles.description}>
                    The exam contains <b>{QUESTION_COUNT}</b> coding questions
                    {langs.length === 1 && ` in ${langs[0]}`},<br />
                    each coding question has a limited time.
                </p>

                {hasExamLanguageChoice(examLanguages) && <DropDown
                    idKey='id'
                    titleKey='title'
                    label='Exam language'
                    dropDownList={examLanguages}
                    containerClassName={styles.examLanguage}
                    selectedItem={examLanguages.find(i => i.id === examLanguage)}
                    setItem={(item: ExamLanguage) => onSelectExamLanguage(item.id)}
                />}

                {!isCameraActive &&
                    <div className={styles.content_warning}>
                        <WarningIcon />
                        <span>Before the exam begins, you must give permission to camera and microphone record the session.</span>
                    </div>}
                {!cameraAccess && <span className={styles.content_link} onClick={onEnableCamera}>Enable camera record</span>}

                {!isScreenRecording &&
                    <div className={styles.content_warning}>
                        <WarningIcon />
                        <span>Before the exam begins, you must give permission to screen record the session.</span>
                    </div>}
                {screenRecordFailed && <span className={styles.content_link} onClick={onStartScreenRecord}>Enable screen record</span>}

                <Button
                    type='Primary'
                    loading={loading}
                    onClick={onStart}
                    className={styles.CTA}
                    text={action === 'resume' ? 'Resume the exam' : 'Start the exam'}
                    disabled={
                        !isCameraActive ||
                        !isScreenRecording ||
                        (hasLanguageChoice && !selectedLang)
                    }
                />

                <div className={styles.extra}>
                    <span className={styles.extra_text}>Please review exam rules before starting the exam</span>
                    <div
                        className={styles.extra_btn}
                        onClick={() => dispatch(setExamRulesModal(true))}
                    >
                        <span>Exams rules</span>
                        <ArrowRightIcon />
                    </div>
                </div>
            </div>
        </div>
    )
};

export default PreExam;
